/**
 * Product gallery thumbnail navigation: match image-list height and enable drag scrolling.
 */
(() => {
  const NAV_SELECTOR = 'product-gallery-navigation.product-gallery__thumbnail-scroller';

  const AXIS_THRESHOLD = 8;
  const DRAG_THRESHOLD = 4;

  function shouldSyncHeight(nav) {
    const gallery = nav.closest('.product-gallery');
    if (!gallery) return false;
    const direction = getComputedStyle(gallery).flexDirection;
    return direction === 'row' || direction === 'row-reverse';
  }

  function syncThumbnailListHeight(nav) {
    const gallery = nav.closest('.product-gallery');
    const imageList = gallery?.querySelector('.product-gallery__image-list');
    const thumbList = nav.closest('.product-gallery__thumbnail-list');
    if (!gallery || !imageList || !thumbList) return;

    if (!shouldSyncHeight(nav)) {
      gallery.style.removeProperty('--product-gallery-thumbnail-list-height');
      thumbList.style.removeProperty('height');
      thumbList.style.removeProperty('max-height');
      thumbList.style.removeProperty('overflow');
      nav.style.removeProperty('height');
      nav.style.removeProperty('max-height');
      nav.style.removeProperty('min-height');
      return;
    }

    const height = Math.ceil(imageList.getBoundingClientRect().height);
    if (height <= 0) return;

    gallery.style.setProperty('--product-gallery-thumbnail-list-height', `${height}px`);
    thumbList.style.height = `${height}px`;
    thumbList.style.maxHeight = `${height}px`;
    thumbList.style.overflow = 'hidden';
    nav.style.height = '100%';
    nav.style.maxHeight = '100%';
    nav.style.minHeight = '0';
  }

  function updateScrollHint(nav) {
    const thumbList = nav.closest('.product-gallery__thumbnail-list');
    if (!thumbList) return;

    const canScrollY = nav.scrollHeight > nav.clientHeight + 1;
    const canScrollX = nav.scrollWidth > nav.clientWidth + 1;
    let hasMore = false;

    if (canScrollY) {
      hasMore = nav.scrollTop + nav.clientHeight < nav.scrollHeight - 1;
    } else if (canScrollX) {
      hasMore = nav.scrollLeft + nav.clientWidth < nav.scrollWidth - 1;
    }

    thumbList.classList.toggle('product-gallery__thumbnail-list--has-more', hasMore);
  }

  function bindScrollHint(nav) {
    if (nav.dataset.thumbnailScrollHint === 'true') return;
    nav.dataset.thumbnailScrollHint = 'true';

    const update = () => updateScrollHint(nav);
    nav.addEventListener('scroll', update, { passive: true });
    const ro = new ResizeObserver(update);
    ro.observe(nav);

    update();
  }

  function bindHeightSync(nav) {
    if (nav.dataset.thumbnailHeightSync === 'true') return;
    nav.dataset.thumbnailHeightSync = 'true';

    const gallery = nav.closest('.product-gallery');
    const imageList = gallery?.querySelector('.product-gallery__image-list');
    const swiper = gallery?.querySelector('product-gallery-swiper');

    const update = () => {
      requestAnimationFrame(() => {
        syncThumbnailListHeight(nav);
        updateScrollHint(nav);
      });
    };
    const ro = new ResizeObserver(update);

    if (imageList) {
      ro.observe(imageList);
      imageList.querySelectorAll('img').forEach((img) => {
        if (!img.complete) {
          img.addEventListener('load', update, { once: true });
        }
      });
    }
    if (swiper) ro.observe(swiper);

    swiper?.addEventListener('carousel:settle', update);
    swiper?.addEventListener('carousel:change', update);
    gallery?.addEventListener('carousel:filter', update);
    window.addEventListener('resize', update, { passive: true });
    window.addEventListener('load', update, { passive: true });

    update();
    setTimeout(update, 100);
    setTimeout(update, 500);
  }

  function bindDragScroll(nav) {
    if (nav.dataset.thumbnailDragScroll === 'true') return;
    nav.dataset.thumbnailDragScroll = 'true';

    let suppressNextClick = false;

    nav.addEventListener(
      'click',
      (event) => {
        if (!suppressNextClick) return;
        suppressNextClick = false;
        event.preventDefault();
        event.stopPropagation();
      },
      true
    );

    nav.addEventListener('pointerdown', (event) => {
      if (event.pointerType === 'mouse' && event.button !== 0) return;

      const canScrollY = nav.scrollHeight > nav.clientHeight;
      const canScrollX = nav.scrollWidth > nav.clientWidth;
      if (!canScrollY && !canScrollX) return;

      const overflowY = nav.scrollHeight - nav.clientHeight;
      const overflowX = nav.scrollWidth - nav.clientWidth;
      const primaryAxis = canScrollY && (!canScrollX || overflowY >= overflowX) ? 'y' : 'x';

      const pointerId = event.pointerId;
      const startX = event.clientX;
      const startY = event.clientY;
      const startScrollLeft = nav.scrollLeft;
      const startScrollTop = nav.scrollTop;
      let axisDecided = false;
      let dragActive = false;
      let didDrag = false;

      const cleanup = () => {
        window.removeEventListener('pointermove', onMove);
        window.removeEventListener('pointerup', onUp);
        window.removeEventListener('pointercancel', onUp);
        nav.classList.remove('is-dragging');
        nav.style.removeProperty('scroll-snap-type');
      };

      const onMove = (ev) => {
        if (ev.pointerId !== pointerId) return;

        const dx = ev.clientX - startX;
        const dy = ev.clientY - startY;

        if (!axisDecided) {
          if (Math.abs(dx) < AXIS_THRESHOLD && Math.abs(dy) < AXIS_THRESHOLD) return;
          axisDecided = true;

          if (primaryAxis === 'y' && Math.abs(dx) > Math.abs(dy)) {
            cleanup();
            return;
          }
          if (primaryAxis === 'x' && Math.abs(dy) > Math.abs(dx)) {
            cleanup();
            return;
          }

          dragActive = true;
          nav.classList.add('is-dragging');
          nav.style.scrollSnapType = 'none';
        }

        if (!dragActive) return;

        if (ev.pointerType !== 'mouse') {
          ev.preventDefault();
        }

        if (primaryAxis === 'y') {
          if (Math.abs(dy) > DRAG_THRESHOLD) didDrag = true;
          nav.scrollTop = startScrollTop - dy;
        } else {
          if (Math.abs(dx) > DRAG_THRESHOLD) didDrag = true;
          nav.scrollLeft = startScrollLeft - dx;
        }

        updateScrollHint(nav);
      };

      const onUp = (ev) => {
        if (ev.pointerId !== pointerId) return;
        cleanup();
        if (didDrag) suppressNextClick = true;
      };

      window.addEventListener('pointermove', onMove);
      window.addEventListener('pointerup', onUp);
      window.addEventListener('pointercancel', onUp);
    });
  }

  function initNav(nav) {
    if (!(nav instanceof HTMLElement)) return;
    bindHeightSync(nav);
    bindDragScroll(nav);
    bindScrollHint(nav);
  }

  function initAll(root = document) {
    root.querySelectorAll(NAV_SELECTOR).forEach(initNav);
  }

  initAll();

  document.addEventListener('shopify:section:load', (event) => {
    initAll(event.target);
  });
})();
